import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Send, Percent, FileText, CheckCircle2, DollarSign,
  ArrowUp, ArrowDown, Calendar as CalIcon, ChevronDown, Filter, TrendingUp,
  AlertTriangle, Upload, Loader2,
} from 'lucide-react';
import { EmptyState } from '../components/mailer/atoms';
import StateHeatmap from '../components/mailer/StateHeatmap';
import DateRangePicker, { rangeToIsoParams, RANGE_PRESETS } from '../components/mailer/DateRangePicker';
import { useTheme } from '../components/layout/ThemeToggle';
import { Calendar } from '../components/ui/calendar';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mailer/dashboard`;

const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(Number(v) % 1 ? 2 : 0)}%`);
const fmtPctShort = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtMoney = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));
const fmtMoneyCompact = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const rangeLabel = (id, customRange) => {
  if (id === 'custom' && customRange?.from) {
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const from = fmt(customRange.from);
    const to = customRange.to ? fmt(customRange.to) : 'Today';
    return `${from} – ${to}`;
  }
  return (RANGE_PRESETS.find((x) => x.id === id) || { label: 'All time' }).label;
};
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

/* ============================================================
   KPI tile — colored icon chip + label + value + delta
   ============================================================ */
function KpiTile({ label, value, sub, deltaPct, deltaPp, icon: Icon, accent, testId }) {
  const hasDelta = deltaPct != null || deltaPp != null;
  const positive = hasDelta && (deltaPct != null ? deltaPct >= 0 : deltaPp >= 0);
  const DeltaIcon = positive ? ArrowUp : ArrowDown;
  // Map the soft `bg-{hue}-50` accent into a saturated solid for the
  // progress bar at the bottom of the card. Keeping the bar color in lockstep
  // with the icon tint is what makes the row read as a coherent palette.
  const BAR_TONE = {
    'bg-sky-50':     '#0ea5e9',
    'bg-amber-50':   '#f59e0b',
    'bg-teal-50':    '#0d9488',
    'bg-violet-50':  '#8b5cf6',
    'bg-purple-50':  '#a855f7',
    'bg-rose-50':    '#fb7185',
    'bg-emerald-50': '#10b981',
  };
  const barColor = BAR_TONE[accent?.bg] || '#a3a3a3';
  // The progress bar's filled width is a visual signal of "how heavy this
  // metric is right now" — anchor on the delta when we have one (positive
  // momentum = fuller bar, capped at 95%), otherwise show a full bar so
  // empty deltas still feel anchored.
  const deltaForBar = deltaPct != null ? deltaPct : deltaPp != null ? deltaPp : 100;
  const widthPct = Math.max(18, Math.min(95, 35 + Math.abs(deltaForBar) * 4));
  return (
    <div
      data-testid={testId}
      className="relative rounded-2xl border hairline bg-white px-4 py-3.5 transition hover:border-neutral-300 hover:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.10)] overflow-hidden"
    >
      {/* Top row: centered icon ring */}
      <div className="flex flex-col items-center">
        <div className={cn('h-10 w-10 rounded-full flex items-center justify-center', accent.bg)}>
          <Icon className={cn('h-[18px] w-[18px]', accent.fg)} strokeWidth={2.2} />
        </div>
        <div className="mt-2.5 text-[9.5px] uppercase tracking-[0.2em] text-neutral-500 font-bold leading-tight">{label}</div>
        <div className="display text-[24px] font-bold tracking-tight text-neutral-900 mt-1.5 leading-none tabular-nums">
          {value}
        </div>
      </div>
      {/* Delta + range — subtle, sits above the bar */}
      <div className="mt-3 flex items-center justify-between text-[10px]">
        <span className="text-neutral-400 font-semibold uppercase tracking-wider">{sub}</span>
        {hasDelta ? (
          <span className={cn('inline-flex items-center gap-0.5 font-bold mono', positive ? 'text-emerald-600' : 'text-red-600')}>
            <DeltaIcon className="h-2.5 w-2.5" strokeWidth={3} />
            {deltaPp != null ? `${Math.abs(deltaPp).toFixed(2)}pp` : `${Math.abs(deltaPct).toFixed(1)}%`}
          </span>
        ) : <span className="text-neutral-300 mono font-bold">—</span>}
      </div>
      {/* Bottom progress accent — solid tone keyed to the icon ring */}
      <div className="absolute left-4 right-4 bottom-2 h-[3px] rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${widthPct}%`, background: barColor }} />
      </div>
    </div>
  );
}

/* ============================================================
   Pipeline Funnel — a SINGLE continuous trapezoid, sliced into
   equal-height horizontal bands. Sides are STRAIGHT diagonals
   (linear taper from wide top to narrow bottom). Widths are
   purely geometric — NOT data-driven — so the silhouette stays
   clean regardless of conversion rates. White hairline dividers
   between bands. Colors are pulled from the dashboard's KPI
   tile palette (sky · violet · amber · teal) so the funnel
   reads as a cohesive part of the page.
   ============================================================ */
const FUNNEL_STAGES = [
  { key: 'total_mailed', label: 'Total Mailed',  bg: '#0ea5e9' }, // sky-500   → Total Mailed KPI
  { key: 'responses',    label: 'Claimed Leads', bg: '#8b5cf6' }, // violet-500 → Response Rate KPI
  { key: 'applications', label: 'Submissions',   bg: '#f59e0b' }, // amber-500 → Submissions KPI
  { key: 'funded',       label: 'Funded Loans',  bg: '#0d9488' }, // teal-600  → Funded Loans KPI
];

function FunnelTrapezoid({ kpis }) {
  const STAGE_H = 78;
  const TOP_W = 96;   // top of the funnel (wide)
  const BOT_W = 38;   // bottom of the funnel (narrow)
  const N = FUNNEL_STAGES.length;
  const totalH = STAGE_H * N;
  // Linear interpolation: width at fractional position p in [0,1]
  const widthAt = (p) => TOP_W - (TOP_W - BOT_W) * p;

  return (
    <div className="relative w-full max-w-[520px] mx-auto" data-testid="dashboard-funnel">
      <svg viewBox={`0 0 100 ${totalH}`} preserveAspectRatio="none" className="block w-full" style={{ height: totalH }}>
        {FUNNEL_STAGES.map((s, i) => {
          const wTop = widthAt(i / N);
          const wBot = widthAt((i + 1) / N);
          const x1Top = (100 - wTop) / 2;
          const x2Top = 100 - x1Top;
          const x1Bot = (100 - wBot) / 2;
          const x2Bot = 100 - x1Bot;
          const yTop = i * STAGE_H;
          const yBot = yTop + STAGE_H;
          return (
            <polygon
              key={s.key}
              points={`${x1Top},${yTop} ${x2Top},${yTop} ${x2Bot},${yBot} ${x1Bot},${yBot}`}
              fill={s.bg}
            />
          );
        })}
        {/* White hairline dividers between bands */}
        {FUNNEL_STAGES.slice(1).map((_, idx) => {
          const i = idx + 1;
          const w = widthAt(i / N);
          const x1 = (100 - w) / 2;
          const x2 = 100 - x1;
          const y = i * STAGE_H;
          return (
            <line key={`div-${i}`} x1={x1} y1={y} x2={x2} y2={y}
              stroke="#ffffff" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          );
        })}
      </svg>
      {/* Labels overlay — one row per band, equal heights */}
      <div className="absolute inset-0 flex flex-col">
        {FUNNEL_STAGES.map((s, i) => {
          const v = kpis[s.key] || 0;
          const pct = i === 0 ? null : (kpis.total_mailed ? (v / kpis.total_mailed) * 100 : 0);
          return (
            <div
              key={s.key}
              className="flex-1 flex items-center justify-center text-center px-3 text-white"
            >
              <div>
                <div className="display text-[22px] font-bold tracking-tight leading-none">
                  {fmtInt(v)}
                  {pct != null && (
                    <span className="ml-1.5 text-[12px] font-semibold opacity-90">({fmtPctShort(pct)})</span>
                  )}
                </div>
                <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] mt-1.5 opacity-95">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Date Range + Filters
   ============================================================ */
function FiltersBar({ leadSource, onLeadSource, state, onState, campaignNames, campaign, onCampaign }) {
  const [open, setOpen] = useState(false);
  const activeCount = [leadSource, state, campaign].filter((v) => v && v !== 'all').length;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="dashboard-filters-trigger"
        className="h-10 px-4 rounded-lg border hairline bg-white hover:border-neutral-400 inline-flex items-center gap-2 text-[12.5px] font-semibold text-neutral-700"
      >
        <Filter className="h-4 w-4 text-neutral-500" />
        Filters
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center h-4 min-w-4 px-1.5 rounded-full bg-neutral-900 text-white text-[10px] font-bold">{activeCount}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1.5 z-30 w-[300px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] p-4 space-y-3">
            <FilterSelect label="Campaign Type" value={leadSource} onChange={onLeadSource} testId="dashboard-filter-leadsource"
              options={[
                { v: 'all', l: 'All types' },
                { v: 'Conventional Mailer', l: 'Conventional' },
                { v: 'FHA Mailer', l: 'FHA' },
                { v: 'VA Mailer', l: 'VA' },
                { v: 'Web Lead', l: 'Web Lead' },
                { v: 'Other', l: 'Other' },
              ]}
            />
            <FilterSelect label="Campaign" value={campaign} onChange={onCampaign} testId="dashboard-filter-campaign"
              options={[{ v: 'all', l: 'All campaigns' }, ...(campaignNames || []).map((c) => ({ v: c, l: c }))]}
            />
            <FilterSelect label="State" value={state} onChange={onState} testId="dashboard-filter-state"
              options={[{ v: 'all', l: 'All states' }, ...STATES.map((s) => ({ v: s, l: s }))]}
            />
            {activeCount > 0 && (
              <button
                type="button"
                onClick={() => { onLeadSource('all'); onState('all'); onCampaign('all'); }}
                className="w-full h-8 rounded-md text-[11.5px] font-semibold text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition"
                data-testid="dashboard-filters-clear"
              >
                Clear filters
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1.5">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full h-9 px-2 rounded-md bg-white border hairline text-[12.5px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

/* ============================================================
   Source chip helpers
   ============================================================ */
const sourceChip = (source) => {
  const s = (source || '').toLowerCase();
  if (s.includes('fha')) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (s.includes('va')) return 'bg-sky-50 text-sky-700 border border-sky-200';
  if (s.includes('conventional')) return 'bg-violet-50 text-violet-700 border border-violet-200';
  if (s.includes('web')) return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-neutral-100 text-neutral-700 border border-neutral-200';
};
const shortSource = (source) => {
  const s = (source || '').replace(' Mailer', '');
  return s || '—';
};
// Small color dot used inline next to campaign names — encodes source type
// without the bulky chip pills that pushed the table off-screen.
const sourceDot = (source) => {
  const s = (source || '').toLowerCase();
  if (s.includes('fha')) return 'bg-emerald-500';
  if (s.includes('va')) return 'bg-sky-500';
  if (s.includes('conventional')) return 'bg-violet-500';
  if (s.includes('web')) return 'bg-amber-500';
  return 'bg-neutral-400';
};

/* ----------------------------------------------------------------
   Response Rate color tier (text-only, no glyph).
     ≥ 0.75%        → green   (good)
     0.50% – 0.74%  → yellow  (okay)
     < 0.50%        → red     (low)
   Formula remains responses / total_mailed × 100.
   ---------------------------------------------------------------- */
const responseRateClass = (v) => {
  if (v == null || isNaN(v)) return 'text-neutral-400';
  if (v >= 0.75) return 'text-emerald-600';
  if (v >= 0.5) return 'text-amber-600';
  return 'text-red-600';
};

/* ============================================================
   Top campaigns + Top states
   ============================================================ */
function TopCampaignsCard({ rows }) {
  const top = rows.slice(0, 6);
  const total = useMemo(() => ({
    sent: rows.reduce((s, r) => s + (r.total_mailed || 0), 0),
    claimed: rows.reduce((s, r) => s + (r.responses || 0), 0),
    submissions: rows.reduce((s, r) => s + (r.submissions || r.applications || 0), 0),
    funded: rows.reduce((s, r) => s + (r.funded || 0), 0),
    revenue: rows.reduce((s, r) => s + (r.total_revenue || 0), 0),
  }), [rows]);
  const totalRespRate = total.sent ? (total.claimed / total.sent) * 100 : 0;

  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b hairline flex items-center justify-between">
        <div className="text-[15px] font-bold text-neutral-900 tracking-tight">Top Campaigns</div>
        <Link to="/mailer/campaigns" className="text-[11.5px] font-semibold text-sky-700 hover:underline">View all</Link>
      </div>
      <table className="w-full text-[11.5px] table-fixed" data-testid="dashboard-top-campaigns">
        <colgroup>
          <col style={{ width: '32%' }} />{/* Campaign */}
          <col style={{ width: '11%' }} />{/* Source */}
          <col style={{ width: '9%' }}  />{/* Sent */}
          <col style={{ width: '9%' }}  />{/* Claimed */}
          <col style={{ width: '10%' }} />{/* Submissions */}
          <col style={{ width: '7%' }}  />{/* Funded */}
          <col style={{ width: '10%' }} />{/* Response */}
          <col style={{ width: '12%' }} />{/* Revenue */}
        </colgroup>
        <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-400 font-bold">
          <tr>
            <th className="px-4 py-2.5">Campaign</th>
            <th className="px-2 py-2.5">Source</th>
            <th className="px-2 py-2.5 text-right">Sent</th>
            <th className="px-2 py-2.5 text-right">Claimed</th>
            <th className="px-2 py-2.5 text-right">Subs</th>
            <th className="px-2 py-2.5 text-right">Funded</th>
            <th className="px-2 py-2.5 text-right">Response</th>
            <th className="px-2 py-2.5 text-right">Revenue</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {top.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-neutral-400">No campaigns yet</td></tr>
          )}
          {top.map((r) => (
            <tr key={r.campaign_name} className="hover:bg-neutral-50/60">
              <td className="px-4 py-2.5">
                <Link
                  to={`/mailer/drilldown?campaign=${encodeURIComponent(r.campaign_name)}`}
                  className="flex items-center gap-2 font-semibold text-sky-700 hover:underline min-w-0"
                  title={`${r.campaign_name} · ${r.lead_source || ''}`}
                >
                  <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', sourceDot(r.lead_source))} />
                  <span className="truncate">{r.campaign_name}</span>
                </Link>
              </td>
              <td className="px-2 py-2.5 text-neutral-600 truncate" title={r.lead_source || ''}>{shortSource(r.lead_source)}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-neutral-700">{fmtInt(r.total_mailed)}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-neutral-700">{fmtInt(r.responses)}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-sky-700 font-semibold">{fmtInt(r.submissions || r.applications)}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-emerald-700 font-bold">{fmtInt(r.funded)}</td>
              <td className={cn('px-2 py-2.5 text-right tabular-nums font-bold', responseRateClass(r.response_rate))}>
                {fmtPct(r.response_rate)}
              </td>
              <td className="px-2 py-2.5 text-right tabular-nums font-bold text-neutral-900">{fmtMoney(r.total_revenue)}</td>
            </tr>
          ))}
          {top.length > 0 && (
            <tr className="bg-neutral-50/40">
              <td className="px-4 py-3 font-bold text-neutral-900 uppercase tracking-wider text-[10px]" colSpan={2}>Total</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-neutral-900">{fmtInt(total.sent)}</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-neutral-900">{fmtInt(total.claimed)}</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-sky-700">{fmtInt(total.submissions)}</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-emerald-700">{fmtInt(total.funded)}</td>
              <td className={cn('px-2 py-3 text-right tabular-nums font-bold', responseRateClass(totalRespRate))}>
                {fmtPct(totalRespRate)}
              </td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-neutral-900">{fmtMoney(total.revenue)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TopStatesCard({ rows }) {
  const top = rows.slice(0, 10);
  const total = useMemo(() => ({
    sent: top.reduce((s, r) => s + (r.total_mailed || 0), 0),
    claimed: top.reduce((s, r) => s + (r.responses || 0), 0),
    submissions: top.reduce((s, r) => s + (r.submissions || r.applications || 0), 0),
    funded: top.reduce((s, r) => s + (r.funded || 0), 0),
    revenue: top.reduce((s, r) => s + (r.total_revenue || 0), 0),
  }), [top]);
  const totalRate = total.sent ? (total.claimed / total.sent) * 100 : 0;

  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b hairline flex items-center justify-between">
        <div className="text-[15px] font-bold text-neutral-900 tracking-tight">
          Response Rate by State <span className="text-[11px] text-neutral-400 font-semibold ml-1">(Top 10)</span>
        </div>
        <Link to="/mailer/states" className="text-[11.5px] font-semibold text-sky-700 hover:underline">View all</Link>
      </div>
      <table className="w-full text-[11.5px] table-fixed" data-testid="dashboard-top-states">
        <colgroup>
          <col style={{ width: '14%' }} />{/* State */}
          <col style={{ width: '14%' }} />{/* Sent */}
          <col style={{ width: '13%' }} />{/* Claimed */}
          <col style={{ width: '13%' }} />{/* Submissions */}
          <col style={{ width: '12%' }} />{/* Funded */}
          <col style={{ width: '14%' }} />{/* Response */}
          <col style={{ width: '20%' }} />{/* Revenue */}
        </colgroup>
        <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-400 font-bold">
          <tr>
            <th className="px-4 py-2.5">State</th>
            <th className="px-2 py-2.5 text-right">Sent</th>
            <th className="px-2 py-2.5 text-right">Claimed</th>
            <th className="px-2 py-2.5 text-right">Subs</th>
            <th className="px-2 py-2.5 text-right">Funded</th>
            <th className="px-2 py-2.5 text-right">Response</th>
            <th className="px-2 py-2.5 text-right">Revenue</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {top.length === 0 && (
            <tr><td colSpan={7} className="px-4 py-6 text-center text-neutral-400">No state data yet</td></tr>
          )}
          {top.map((r) => (
            <tr key={r.state} className="hover:bg-neutral-50/60">
              <td className="px-4 py-2.5 font-bold text-neutral-900">{r.state}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-neutral-700">{fmtInt(r.total_mailed)}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-neutral-700">{fmtInt(r.responses)}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-sky-700 font-semibold">{fmtInt(r.submissions || r.applications)}</td>
              <td className="px-2 py-2.5 text-right tabular-nums text-emerald-700 font-bold">{fmtInt(r.funded)}</td>
              <td className={cn('px-2 py-2.5 text-right tabular-nums font-bold', responseRateClass(r.response_rate))}>
                {fmtPct(r.response_rate)}
              </td>
              <td className="px-2 py-2.5 text-right tabular-nums font-bold text-neutral-900">{fmtMoney(r.total_revenue)}</td>
            </tr>
          ))}
          {top.length > 0 && (
            <tr className="bg-neutral-50/40">
              <td className="px-4 py-3 font-bold text-neutral-900 uppercase tracking-wider text-[10px]">Total (Top 10)</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-neutral-900">{fmtInt(total.sent)}</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-neutral-900">{fmtInt(total.claimed)}</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-sky-700">{fmtInt(total.submissions)}</td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-emerald-700">{fmtInt(total.funded)}</td>
              <td className={cn('px-2 py-3 text-right tabular-nums font-bold', responseRateClass(totalRate))}>
                {fmtPct(totalRate)}
              </td>
              <td className="px-2 py-3 text-right tabular-nums font-bold text-neutral-900">{fmtMoney(total.revenue)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================
   Pipeline Overview = funnel + side table + Rev/1k + ROI tiles
   ============================================================ */
function PipelineOverviewCard({ kpis }) {
  // One row per stage. Dot color mirrors the funnel band so the visual
  // language stays consistent across the card. Count is bumped to display
  // weight, label sits beneath in quiet uppercase like a KPI tile.
  const stages = [
    { label: 'Total Mailed',  count: kpis.total_mailed, dot: 'bg-sky-500' },
    { label: 'Claimed Leads', count: kpis.responses,    dot: 'bg-violet-500' },
    { label: 'Submissions',   count: kpis.applications, dot: 'bg-amber-500' },
    { label: 'Funded Loans',  count: kpis.funded,       dot: 'bg-teal-600' },
  ];
  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b hairline">
        <div className="text-[15px] font-bold text-neutral-900 tracking-tight">Pipeline Overview</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-5">
        <div className="lg:col-span-3 flex items-center"><FunnelTrapezoid kpis={kpis} /></div>
        <div className="lg:col-span-2 flex flex-col justify-center">
          <ul className="divide-y divide-neutral-100" data-testid="pipeline-stage-list">
            {stages.map((s) => (
              <li key={s.label} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-2.5">
                  <span className={cn('h-2 w-2 rounded-full shrink-0', s.dot)} />
                  <span className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{s.label}</span>
                </div>
                <span className="text-[14px] font-bold tabular-nums text-neutral-900 leading-none">
                  {fmtInt(s.count)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Map card — theme-aware so dark mode picks up the blue→green
   ramp the broker specified.
   ============================================================ */
function HeatmapCard({ states }) {
  const [theme] = useTheme();
  return (
    <div className="rounded-2xl border hairline bg-white overflow-hidden">
      <div className="px-5 py-3.5 border-b hairline flex items-center justify-between">
        <div className="display text-[16px] font-bold text-neutral-900 tracking-tight">Response Rate by State</div>
        <Link to="/mailer/states" className="text-[12px] font-semibold text-sky-700 hover:underline">View full report</Link>
      </div>
      <div className="p-2">
        <StateHeatmap rows={states} metric="response_rate" height={360} theme={theme === 'dark' ? 'dark' : 'light'} />
      </div>
    </div>
  );
}

/* ============================================================
   Page
   ============================================================ */

/**
 * Renders when `total_imported` (every lead with a mailer blob) exceeds
 * `total_mailed` (the strict, address-keyed subset). Those "missing"
 * leads were imported but couldn't be address-keyed because their CSV
 * row was missing street/city/state/zip. The admin can hit the backfill
 * endpoint right here to recover them.
 */
function DataQualityAlertCard({ kpis, totalImported, onBackfillComplete }) {
  // ── DEPRECATED ──
  // The Data Quality alert + manual backfill button have been retired.
  // The Mailer Dashboard now counts every lead with `mailer.imported = True`,
  // which is auto-stamped at upload time AND auto-backfilled on backend
  // boot. The `total_imported` vs `total_mailed` gap that used to surface
  // here no longer occurs in normal operation.
  //
  // The component is intentionally kept as a no-op so the call site
  // doesn't have to be touched; if a future regression ever causes the
  // gap to re-appear, you can replace the `return null` below with the
  // historical UI and the diagnostic still works.
  return null;
  // eslint-disable-next-line no-unreachable
  const ti = Number(totalImported || 0);
  const tm = Number(kpis?.total_mailed || 0);
  const gap = ti - tm;
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (gap <= 0) return null;

  const runBackfill = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/mailer/maintenance/backfill-address-keys`);
      setResult(r.data);
      if (onBackfillComplete) onBackfillComplete();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Backfill failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 flex items-start gap-4 flex-wrap"
      data-testid="data-quality-alert"
    >
      <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
        <AlertTriangle className="h-4.5 w-4.5 text-amber-700" />
      </div>
      <div className="flex-1 min-w-[260px]">
        <div className="text-[13.5px] font-bold text-amber-900">
          {fmtInt(gap)} imported leads aren&apos;t showing on the dashboard
        </div>
        <div className="text-[12.5px] text-amber-800 mt-0.5 leading-relaxed">
          Your last CSV uploads created <span className="font-bold">{fmtInt(ti)}</span> leads,
          but only <span className="font-bold">{fmtInt(tm)}</span> have a complete property
          address (street + city + state + ZIP). Click below to rebuild the missing address keys
          from any partial address data already stored on those leads.
        </div>
        {result && (
          <div className="mt-2 text-[12px] text-amber-900 bg-amber-100 rounded-md px-2.5 py-1.5" data-testid="backfill-result">
            Scanned <b>{fmtInt(result.scanned)}</b> · Recovered <b>{fmtInt(result.backfilled)}</b>
            {result.still_missing > 0 && <> · Still missing <b>{fmtInt(result.still_missing)}</b> (these rows have no usable address)</>}
          </div>
        )}
        {error && (
          <div className="mt-2 text-[12px] text-rose-800 bg-rose-100 rounded-md px-2.5 py-1.5" data-testid="backfill-error">
            {error}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={runBackfill}
        disabled={running}
        className="h-9 px-4 rounded-md bg-amber-900 hover:bg-amber-950 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
        data-testid="run-backfill-btn"
      >
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {running ? 'Recovering…' : 'Run backfill'}
      </button>
    </div>
  );
}

export default function MailerDashboard() {
  const [kpis, setKpis] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [states, setStates] = useState([]);
  const [rangeId, setRangeId] = useState('all');
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [leadSource, setLeadSource] = useState('all');
  const [state, setState] = useState('all');
  const [campaign, setCampaign] = useState('all');
  const [loading, setLoading] = useState(true);
  const [allCampaignNames, setAllCampaignNames] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [totalImported, setTotalImported] = useState(null);

  const params = useMemo(() => {
    const out = { ...rangeToIsoParams({ rangeId, customRange }) };
    if (leadSource !== 'all') out.lead_source = leadSource;
    if (state !== 'all') out.state = state;
    if (campaign !== 'all') out.campaign = campaign;
    return out;
  }, [rangeId, customRange, leadSource, state, campaign]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/kpis`, { params }),
      axios.get(`${API}/campaigns`, { params: { ...params, campaign: undefined } }),
      axios.get(`${API}/states`, { params }),
    ]).then(([k, c, s]) => {
      if (cancelled) return;
      setKpis(k.data);
      setCampaigns(c.data || []);
      setStates(s.data || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    // Lazy companion fetch — runs in parallel but its slowness/failure
    // never blocks the main dashboard render. The Data Quality alert
    // card only appears once this resolves with `total_imported > total_mailed`.
    axios.get(`${API}/total-imported`, { params })
      .then((r) => { if (!cancelled) setTotalImported(Number(r?.data?.total_imported || 0)); })
      .catch(() => { if (!cancelled) setTotalImported(null); });
    return () => { cancelled = true; };
  }, [params, reloadKey]);

  useEffect(() => {
    axios.get(`${API}/campaigns-list`).then((r) => setAllCampaignNames(r.data || [])).catch(() => {});
  }, []);

  if (loading && !kpis) {
    return (
      <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto">
        <div className="h-64 rounded-2xl border hairline bg-white animate-pulse" />
      </div>
    );
  }

  if (!kpis || kpis.total_mailed === 0) {
    const hasOrphanImports = Number(totalImported || 0) > 0;
    return (
      <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto space-y-6" data-testid="mailer-dashboard-page">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <DateRangePicker
            value={{ rangeId, customRange }}
            onApply={(v) => { setRangeId(v.rangeId); setCustomRange(v.customRange); }}
            testIdPrefix="dashboard-daterange"
          />
          <FiltersBar
            leadSource={leadSource} onLeadSource={setLeadSource}
            state={state} onState={setState}
            campaignNames={allCampaignNames} campaign={campaign} onCampaign={setCampaign}
          />
        </div>
        {hasOrphanImports
          ? <DataQualityAlertCard kpis={kpis} totalImported={totalImported} onBackfillComplete={() => setReloadKey((k) => k + 1)} />
          : <EmptyState />}
      </div>
    );
  }

  const d = kpis.deltas || {};
  const subLabel = rangeLabel(rangeId, customRange);

  const tiles = [
    { label: 'Total Mailed',  value: fmtInt(kpis.total_mailed),                              sub: subLabel, icon: Send,         accent: { bg: 'bg-sky-50',     fg: 'text-sky-600'     }, deltaPct: d.total_mailed, testId: 'kpi-mailers-sent' },
    { label: 'Submissions',   value: fmtInt(kpis.applications),                              sub: subLabel, icon: FileText,     accent: { bg: 'bg-amber-50',   fg: 'text-amber-600'   }, deltaPct: d.applications, testId: 'kpi-applications' },
    { label: 'Funded Loans',  value: fmtInt(kpis.funded),                                    sub: subLabel, icon: CheckCircle2, accent: { bg: 'bg-teal-50',    fg: 'text-teal-600'    }, deltaPct: d.funded,       testId: 'kpi-funded-loans' },
    { label: 'Response Rate', value: fmtPct(kpis.response_rate),                             sub: subLabel, icon: Percent,      accent: { bg: 'bg-violet-50',  fg: 'text-violet-600'  }, deltaPp: d.response_rate, testId: 'kpi-response-rate' },
    { label: 'Revenue',       value: fmtMoneyCompact(kpis.total_revenue),                    sub: subLabel, icon: DollarSign,   accent: { bg: 'bg-purple-50',  fg: 'text-purple-600'  }, deltaPct: d.total_revenue,testId: 'kpi-revenue' },
  ];

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto space-y-6" data-testid="mailer-dashboard-page">
      {/* Controls — page title lives on the MailerLayout header above. */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <DateRangePicker
          value={{ rangeId, customRange }}
          onApply={(v) => { setRangeId(v.rangeId); setCustomRange(v.customRange); }}
          testIdPrefix="dashboard-daterange"
        />
        <FiltersBar
          leadSource={leadSource} onLeadSource={setLeadSource}
          state={state} onState={setState}
          campaignNames={allCampaignNames} campaign={campaign} onCampaign={setCampaign}
        />
      </div>

      {/* Data quality alert — surfaces silently-dropped imports */}
      <DataQualityAlertCard kpis={kpis} totalImported={totalImported} onBackfillComplete={() => setReloadKey((k) => k + 1)} />

      {/* KPI row — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {tiles.map((t) => <KpiTile key={t.label} {...t} />)}
      </div>

      {/* Pipeline + Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <PipelineOverviewCard kpis={kpis} />
        <HeatmapCard states={states} />
      </div>

      {/* Bottom tables — Top Campaigns gets the full width since it's the
          denser table; Top States lives below it. */}
      <TopCampaignsCard rows={campaigns} />
      <TopStatesCard rows={states} />
    </div>
  );
}
